import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { CompanyDetailsFooter } from '@/components/CompanyDetailsFooter';
import { S3_BUCKET_URL } from '@/constants';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import {
  OptionItemSprite,
  OptionItemSpriteList,
} from '@/pages/Onboarding/components/OptionItemSprite/OptionItemSprite';
import { useOnboarding } from '@/pages/Onboarding/context';
import {
  OwnAiTitle,
  OwnAiTitleHighlight,
} from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { AGE_OPTIONS } from '@/pages/Onboarding/options/Age/AGE_OPTIONS';
import { AgeOptionIdType } from '@/pages/Onboarding/options/Age/types';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../../types';

import { ScreenAgeStyles } from './Age.styles';

const getFemaleImageSrc = (ageId: AgeOptionIdType) =>
  `${S3_BUCKET_URL}/layout-own-ai/age/sprite-${ageId}.png`;

const getMaleImageSrc = (ageId: string) =>
  `${S3_BUCKET_URL}/layout-own-ai-man/age/sprite-${ageId}.png`;

export const ScreenAge: React.FC = () => {
  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const age = form.watch('age');
  const gender = form.watch('gender');

  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Age',
      });
    }
    AGE_OPTIONS.forEach((opt) => {
      preloadImage(
        gender === 'male'
          ? getMaleImageSrc(opt.id)
          : getFemaleImageSrc(opt.id as AgeOptionIdType),
      );
    });
  }, [gender]);

  const handleSelect = (option: OnbOptionType<LayoutDataType['age']>) => {
    form.setValue('age', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        age: option.title,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  const getImageSrc = (optionId: string) =>
    gender === 'male'
      ? getMaleImageSrc(optionId)
      : getFemaleImageSrc(optionId as AgeOptionIdType);

  return (
    <ScreenAgeStyles>
      <OnboardingNavigation />

      <OwnAiTitle className="local-title">
        Choose your AI partner's <OwnAiTitleHighlight>age</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSpriteList>
          {AGE_OPTIONS.map((option) => (
            <OptionItemSprite
              key={option.id}
              alt="age"
              isSelected={age === option.id}
              onClick={() => handleSelect(option)}
              spriteSrc={getImageSrc(option.id)}
              title={option.title}
              TEST_ID={`age-${option.id}`}
              show
            />
          ))}
        </OptionItemSpriteList>
      </IonContent>

      <CompanyDetailsFooter extraPadding />
    </ScreenAgeStyles>
  );
};
